/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
	AdvancedFilters,
	EnvironmentalImpactByApplication
} from "../models/http/application/environmental-impact-by-application.model";
import { LineChart } from "../models/http/charts/line-chart/line-chart.model";
import {
	FilterApplication,
	FilterPageResponse,
	FilterRequest
} from "../models/http/dashboard/filter-page-response.model";
import { KPI } from "../models/http/dashboard/kpi.model";
import { TotalMonths } from "../models/http/dashboard/total-months.model";
import { ChartResponse } from "../models/ui/charts.model";
import { DashboardSoftwareAssetsHttpService } from "./http/dashboard-sofware-assets-http.service";

@Injectable({
	providedIn: "root"
})
export class DashboardSoftwareAssetsService {
	constructor(private _dashboardGeneralHttpService: DashboardSoftwareAssetsHttpService) {}

	/**
	 * Retrieves CO2 emissions data for each app for a line chart within the specified date range .
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the line chart data for CO2 emissions.
	 */
	public getAppsPerformance(filters: AdvancedFilters): Observable<ChartResponse<LineChart>> {
		return this._dashboardGeneralHttpService.getAppsPerformance(filters);
	}

	public getAppsWaterConsumption(
		startDate: string,
		endDate: string,
		applicationId: string[],
		layerId: string[],
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		return this._dashboardGeneralHttpService.getAppsWaterConsumption(
			startDate,
			endDate,
			applicationId,
			layerId,
			tenantId
		);
	}

	/**
	 * Retrieves the total CO2 KPI.
	 *
	 * @param filters - The AdvancedFilters request body.
	 * @returns An Observable that emits the KPI data.
	 */
	public getTotalCO2(filters: AdvancedFilters): Observable<KPI> {
		return this._dashboardGeneralHttpService.getTotalCO2(filters);
	}

	public getTotalEmissions(startDate: string, endDate?: string): Observable<TotalMonths> {
		return this._dashboardGeneralHttpService.getTotalEmissions(startDate, endDate);
	}

	/**
	 * Exports the response data in CSV format.
	 *
	 * @param filters - The AdvancedFilters payload as defined in the cursor-based filters API spec.
	 * @returns An observable that emits the response from the export service.
	 */
	public exportResponseInCsv(filters: AdvancedFilters) {
		return this._dashboardGeneralHttpService.exportResponseInCsv(filters);
	}

	/**
	 * Exports the response data in YAML format for the specified date range, application IDs, and tenant ID.
	 *
	 * @param startDate - The start date for the data export in YYYY-MM-DD format.
	 * @param endDate - The end date for the data export in YYYY-MM-DD format.
	 * @param applicationId - An array of application IDs to filter the data.
	 * @param tenantId - The tenant ID to filter the data.
	 * @returns An observable that emits the response from the export service.
	 */
	public exportResponseManifest(
		startDate: string,
		endDate: string,
		applicationId: string[],
		tenantId: string
	) {
		return this._dashboardGeneralHttpService.exportResponseManifest(
			startDate,
			endDate,
			applicationId[0],
			tenantId
		);
	}

	public getEnvironmentalImpactByApplication(
		filters: AdvancedFilters
	): Observable<EnvironmentalImpactByApplication> {
		return this._dashboardGeneralHttpService.getEnvironmentalImpactByApplication(filters);
	}

	/** Paginated application IDs — GET, no body. */
	public getApplicationsIds(
		search: string,
		cursor: string | null,
		size: number = 20
	): Observable<FilterPageResponse<FilterApplication>> {
		return this._dashboardGeneralHttpService.getApplicationsIds(search, cursor, size);
	}

	/** Paginated layer IDs scoped to the given applications — POST. */
	public getLayerIds(
		body: FilterRequest,
		search: string,
		cursor: string | null,
		size: number
	): Observable<FilterPageResponse<string>> {
		return this._dashboardGeneralHttpService.getLayerIds(body, search, cursor, size);
	}
}
